import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useEffect } from "react";

interface CashFlowChartProps {
  period: "month" | "quarter" | "year";
  startDate: string;
  endDate: string;
}

export function CashFlowChart({ period, startDate, endDate }: CashFlowChartProps) {
  const queryClient = useQueryClient();
  
  // Set up real-time subscription for transactions
  useEffect(() => {
    const channel = supabase
      .channel('cashflow-updates')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'transactions' },
        (payload) => {
          console.log('CashFlow detected transaction change:', payload.eventType);
          // Invalidate and refetch data when transactions change
          queryClient.invalidateQueries({ 
            queryKey: ["cash-flow", period, startDate, endDate] 
          });
        }
      )
      .subscribe((status) => {
        console.log('CashFlow real-time subscription status:', status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [period, startDate, endDate, queryClient]);

  const { data: cashFlow, isLoading } = useQuery({
    queryKey: ["cash-flow", period, startDate, endDate],
    queryFn: async () => {
      let query = supabase
        .from("transactions")
        .select("type, amount, created_at")
        .order("created_at", { ascending: true });

      if (startDate) {
        query = query.gte("created_at", startDate);
      }
      if (endDate) {
        query = query.lte("created_at", endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Group transactions by date
      const groupedData = data.reduce((acc: any, transaction) => {
        const date = new Date(transaction.created_at).toISOString().split("T")[0];
        if (!acc[date]) {
          acc[date] = { date, income: 0, expenses: 0 };
        }
        if (transaction.type === "entrada") {
          acc[date].income += Number(transaction.amount);
        } else {
          acc[date].expenses += Number(transaction.amount);
        }
        return acc;
      }, {});

      return Object.values(groupedData);
    },
    // Keep cached data from becoming stale too quickly to prevent unnecessary rerenders
    staleTime: 10000, // 10 seconds
  });

  if (isLoading) {
    return <div>Carregando...</div>;
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart data={cashFlow}>
        <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
        <XAxis
          dataKey="date"
          stroke="#8A9099"
          tick={{ fill: "#8A9099" }}
        />
        <YAxis
          stroke="#8A9099"
          tick={{ fill: "#8A9099" }}
          tickFormatter={(value) => formatCurrency(value)}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: "#1A1F2C",
            border: "1px solid #404040",
          }}
          formatter={(value: number) => formatCurrency(value)}
        />
        <Legend />
        <Line
          type="monotone"
          dataKey="income"
          name="Entradas"
          stroke="#22C55E"
          strokeWidth={2}
          dot={false}
        />
        <Line
          type="monotone"
          dataKey="expenses"
          name="Saídas"
          stroke="#EF4444"
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
